// src/admin/AdminManagement.js
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate, Outlet, NavLink, useLocation } from 'react-router-dom';
import { StudentContext } from '../StudentContext';
import TopNav from '../components/TopNav';
import { firestore } from '../firebaseConfig';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import './AdminManagement.css';

const cleanVietnameseId = (str) => {
  if (!str) return '';
  let slug = str.toString().toLowerCase();
  slug = slug.replace(/đ/g, 'd');
  slug = slug.replace(/[áàảãạăắằẳẵặâấầẩẫậ]/g, 'a');
  slug = slug.replace(/[éèẻẽẹêếềểễệ]/g, 'e');
  slug = slug.replace(/[íìỉĩị]/g, 'i');
  slug = slug.replace(/[óòỏõọôốồổỗộơớờởỡợ]/g, 'o');
  slug = slug.replace(/[úùủũụưứừửữự]/g, 'u');
  slug = slug.replace(/[ýỳỷỹỵ]/g, 'y');
  slug = slug.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  slug = slug.replace(/[^a-z0-9]/g, '-');
  slug = slug.replace(/-+/g, '-');
  slug = slug.replace(/^-+|-+$/g, '');
  return slug;
};

// 🌟 THÊM TAB "Quản lý tất cả điểm danh" VÀO NHÓM ATTENDANCE
const TAB_GROUPS = [
  {
    id: 'students',
    label: '👧 Quản lý Thiếu nhi',
    items: [
      { path: 'student-management', label: 'Quản lý thiếu nhi' },
      { path: 'class-transfer-history', label: 'Lịch sử chuyển lớp' },
      { path: 'add-new-student', label: 'Thêm thiếu nhi mới' },
      { path: 'print-cards', label: 'In thẻ thiếu nhi' },
      { path: 'print-class-list', label: 'In danh sách lớp' },
      { path: 'tuition-management', label: 'Quản lý học phí' },
      { path: 'print-tuition-list', label: 'In danh sách đóng tiền' },
      { path: 'print-dropped-out', label: 'In DS thiếu nhi nghỉ luôn' },
    ]
  },
  {
    id: 'catechists',
    label: '👨‍🏫 Quản lý Giáo lý viên',
    items: [
      { path: 'catechist-management', label: 'Quản lý Giáo lý viên' },
      { path: 'catechist-substitutes', label: 'Phân công phụ lớp' },
      { path: 'catechist-status', label: 'Quản lý trạng thái GLV' },
      { path: 'catechist-cv', label: 'Sơ yếu lý lịch GLV' },
      { path: 'catechist-attendance', label: 'Điểm danh Giáo Lý Viên' },
      { path: 'catechist-gifts', label: 'Quà tặng GLV' },
    ]
  },
  {
    id: 'attendance',
    label: '⛪ Điểm danh',
    items: [
      { path: 'attendance-log', label: 'Quản lý quy trình điểm danh' },
      { path: 'all-attendance', label: 'Quản lý tất cả điểm danh' }, // 🌟 MỚI THÊM
      { path: 'early-attendance', label: 'Thống kê Đi Lễ' },
      { path: 'attendance-assignment', label: 'Phân công điểm danh' },
      { path: 'supplemental-attendance', label: 'Bổ sung điểm danh' },
      { path: 'attendance-dates', label: 'Quản lý ngày điểm danh' },
    ]
  },
  {
    id: 'academics',
    label: '⛪ Học tập',
    items: [
      { path: 'transcript-management', label: 'Quản lý bảng điểm' },
      { path: 'print-transcripts', label: 'In bảng điểm' },
      { path: 'quiz-statistics', label: 'Thống kê Đố vui' },
      { path: 'all-transcripts', label: 'Tất cả bảng điểm' },
      { path: 'transcript-history', label: 'Lịch sử sửa điểm' },
    ]
  },
  {
    id: 'system',
    label: '⚙️ Hệ thống & Khác',
    items: [
      { path: 'academic-years', label: 'Quản lý Niên khóa' },
      { path: 'update-holy-name', label: 'Cập nhật Tên Thánh' },
      { path: 'student-promotion', label: 'Chuyển lớp niên khóa' },
      { path: 'event-management', label: 'Quản lý Sự kiện' },
      { path: 'chatbot-management', label: 'Quản lý Chatbot' },
      { path: 'data-migration', label: 'Di trú dữ liệu RTDB' },
    ]
  }
];

const AdminManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { studentsData, isDataLoaded, loadAndFilterData } = useContext(StudentContext);

  const [selectedClass, setSelectedClass] = useState('all');
  const [openBlocks, setOpenBlocks] = useState({});
  const [dynamicBlocks, setDynamicBlocks] = useState({});
  const [currentYearInfo, setCurrentYearInfo] = useState({ id: '', name: '' });
  const [sidebarLoading, setSidebarLoading] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [activeGroupId, setActiveGroupId] = useState('attendance');

  useEffect(() => {
    const userRole = localStorage.getItem('userRole') || 'catechist';
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    if (!isLoggedIn || userRole !== 'admin') {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (!isDataLoaded) {
      loadAndFilterData();
    }
  }, [isDataLoaded, loadAndFilterData]);

  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname, selectedClass]);

  useEffect(() => {
    const currentSubPath = location.pathname.split('/admin-management/')[1] || '';
    if (!currentSubPath) return;

    const matchedGroup = TAB_GROUPS.find(group =>
      group.items.some(item => currentSubPath.startsWith(item.path))
    );

    if (matchedGroup) {
      setActiveGroupId(matchedGroup.id);
    }
  }, [location.pathname]);

  useEffect(() => {
    const fetchCurrentYearStructure = async () => {
      try {
        setSidebarLoading(true);

        const yearQuery = query(collection(firestore, 'academic_years'), where('isCurrent', '==', true));
        let yearSnap = await getDocs(yearQuery);
        let currentYearDoc = yearSnap.docs[0];

        if (!currentYearDoc) {
          const allYearsSnap = await getDocs(query(collection(firestore, 'academic_years'), orderBy('startDate', 'desc')));
          if (!allYearsSnap.empty) {
            currentYearDoc = allYearsSnap.docs[0];
          }
        }

        if (!currentYearDoc) {
          setSidebarLoading(false);
          return;
        }

        const yearId = currentYearDoc.id;
        const yearData = currentYearDoc.data();
        setCurrentYearInfo({ id: yearId, name: yearData.name || yearId });

        const gradesSnap = await getDocs(collection(firestore, `academic_years/${yearId}/grades`));
        const gradesList = gradesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        const gradeOrderMap = {};
        gradesList.forEach(g => {
          gradeOrderMap[g.id] = g.sortOrder || 0;
        });

        const classesSnap = await getDocs(collection(firestore, `academic_years/${yearId}/classes`));
        const classesList = classesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        classesList.sort((a, b) => {
          const orderA = gradeOrderMap[a.gradeId] ?? 999;
          const orderB = gradeOrderMap[b.gradeId] ?? 999;
          if (orderA !== orderB) return orderA - orderB;
          return (a.sortOrder || 0) - (b.sortOrder || 0);
        });

        const blocksObj = {};
        classesList.forEach(c => {
          const groupName = c.group || 'Khác';
          const groupKey = cleanVietnameseId(groupName) || 'khac';

          if (!blocksObj[groupKey]) {
            blocksObj[groupKey] = {
              groupName: groupName,
              classes: []
            };
          }
          blocksObj[groupKey].classes.push({
            id: c.id,
            name: c.name
          });
        });

        setDynamicBlocks(blocksObj);

        const firstGroupKey = Object.keys(blocksObj)[0];
        if (firstGroupKey) {
          setOpenBlocks({ [firstGroupKey]: true });
        }

      } catch (err) {
        console.error("Lỗi khi tải cấu trúc sidebar từ Firestore:", err);
      } finally {
        setSidebarLoading(false);
      }
    };

    fetchCurrentYearStructure();
  }, []);

  const toggleBlock = (blockId) => {
    setOpenBlocks((prev) => ({ ...prev, [blockId]: !prev[blockId] }));
  };

  const handleSelectClass = (classId) => {
    setSelectedClass(classId);
    setIsMobileSidebarOpen(false);
  };

  const handleParentGroupClick = (group) => {
    setActiveGroupId(group.id);
    const currentSubPath = location.pathname.split('/admin-management/')[1] || '';
    const isCurrentPathInGroup = group.items.some(item => currentSubPath.startsWith(item.path));

    if (!isCurrentPathInGroup && group.items.length > 0) {
      navigate(group.items[0].path);
    }
  };

  const selectedClassNameDisplay = useMemo(() => {
    if (selectedClass === 'all') return 'Tất cả các lớp';
    for (const key in dynamicBlocks) {
      const found = dynamicBlocks[key].classes.find(c => c.id === selectedClass);
      if (found) return found.name;
    }
    return selectedClass;
  }, [selectedClass, dynamicBlocks]);

  const allStudentsForOutlet = useMemo(() =>
    Object.keys(studentsData || {})
      .flatMap((classId) =>
        (studentsData[classId] || []).map((student) => ({
          ...student,
          classId: classId,
        }))
      )
      .filter((student) => {
        if (selectedClass === 'all') return true;
        return student.classId === selectedClass;
      }),
    [studentsData, selectedClass]);

  const activeGroup = useMemo(() => {
    return TAB_GROUPS.find(g => g.id === activeGroupId) || TAB_GROUPS[0];
  }, [activeGroupId]);

  const renderNavTabs = () => {
    return (
      <div className="admin-nav-wrapper">
        <nav className="admin-parent-tabs">
          <ul className="admin-parent-list">
            {TAB_GROUPS.map((group) => (
              <li key={group.id}>
                <button
                  type="button"
                  className={`admin-parent-tab-btn ${activeGroupId === group.id ? 'active' : ''}`}
                  onClick={() => handleParentGroupClick(group)}
                >
                  {group.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <nav className="admin-child-tabs">
          <ul className="admin-child-list">
            {activeGroup.items.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  className={({ isActive }) => `admin-child-tab ${isActive ? 'active' : ''}`}
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    );
  };

  const renderSidebar = () => {
    return (
      <>
        {isMobileSidebarOpen && (
          <div
            className="admin-sidebar-backdrop"
            onClick={() => setIsMobileSidebarOpen(false)}
          ></div>
        )}

        <aside className={`admin-sidebar ${isMobileSidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-mobile-header">
            <h3>📂 Chọn Lớp Học</h3>
            <button
              type="button"
              className="sidebar-close-btn"
              onClick={() => setIsMobileSidebarOpen(false)}
            >
              ✕
            </button>
          </div>

          {currentYearInfo.name && (
            <div className="current-year-badge">
              ⭐ {currentYearInfo.name}
            </div>
          )}

          <ul className="sidebar-group-list">
            <li className="sidebar-group-item">
              <button
                onClick={() => handleSelectClass('all')}
                className={`sidebar-btn all-classes-btn ${selectedClass === 'all' ? 'active' : ''}`}
              >
                Tất cả các lớp
              </button>
            </li>

            {sidebarLoading ? (
              <li className="sidebar-msg">⏳ Đang tải danh sách lớp...</li>
            ) : Object.keys(dynamicBlocks).length === 0 ? (
              <li className="sidebar-msg empty">Chưa có lớp nào trong niên khóa này.</li>
            ) : (
              Object.entries(dynamicBlocks).map(([groupKey, groupData]) => (
                <li key={groupKey} className="sidebar-group-item">
                  <button
                    onClick={() => toggleBlock(groupKey)}
                    className="sidebar-btn block-btn"
                  >
                    <span>{groupData.groupName}</span>
                    <span className={`block-arrow ${openBlocks[groupKey] ? 'open' : ''}`}>▸</span>
                  </button>

                  {openBlocks[groupKey] && (
                    <ul className="sidebar-submenu">
                      {groupData.classes.map((classItem) => (
                        <li key={classItem.id}>
                          <button
                            onClick={() => handleSelectClass(classItem.id)}
                            className={`sidebar-btn class-btn ${selectedClass === classItem.id ? 'active' : ''}`}
                          >
                            {classItem.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))
            )}
          </ul>
        </aside>
      </>
    );
  };

  // 🌟 HIỂN THỊ SIDEBAR KHI VÀO TAB "all-attendance"
  const showSidebar =
    location.pathname.startsWith('/admin-management/print-cards') ||
    location.pathname.startsWith('/admin-management/early-attendance') ||
    location.pathname.startsWith('/admin-management/print-transcripts') ||
    location.pathname.startsWith('/admin-management/student-management') ||
    location.pathname.startsWith('/admin-management/quiz-statistics') ||
    location.pathname.startsWith('/admin-management/print-class-list') ||
    location.pathname.startsWith('/admin-management/tuition-management') ||
    location.pathname.startsWith('/admin-management/print-tuition-list') ||
    location.pathname.startsWith('/admin-management/print-dropped-out') ||
    location.pathname.startsWith('/admin-management/class-transfer-history') ||
    location.pathname.startsWith('/admin-management/all-attendance')||
    location.pathname.startsWith('/admin-management/transcript-history'); //
    

  return (
    <div className="admin-layout">
      <TopNav />
      {renderNavTabs()}
      <div className="admin-main-wrapper">
        {showSidebar && (
          <>
            <div className="mobile-filter-bar">
              <button
                type="button"
                className="mobile-sidebar-toggle-btn"
                onClick={() => setIsMobileSidebarOpen(true)}
              >
                🏫 Chọn Lớp: <strong>{selectedClassNameDisplay}</strong> ▾
              </button>
            </div>
            {renderSidebar()}
          </>
        )}
        <main className="admin-content-area">
          <Outlet context={{ allStudentsForOutlet, selectedClassForOutlet: selectedClass, dynamicBlocks }} />
        </main>
      </div>
    </div>
  );
};

export default AdminManagement;